import { printDebug } from '@/lib/printDebug'

/**
 * Single seam between the web app and the Bhookly desktop (Electron) shell.
 *
 * In a normal browser, printing goes through an off-screen iframe + window.print()
 * and lands on the OS default printer (silent only if launched with
 * --kiosk-printing). Inside the desktop app, window.bhookly lets us print
 * silently AND target a specific printer per job — which is what makes
 * per-station routing (bar slip → bar printer, kitchen slip → kitchen printer)
 * possible at all.
 *
 * Callers feature-detect via desktop()/desktopPrint() so the SAME bundle runs
 * in the browser and in the desktop app with no build-time branching.
 */

export interface DesktopPrintResult {
  ok: boolean
  error?: string
}

export interface DesktopBridge {
  isDesktop: true
  appVersion(): Promise<string>
  print(req: {
    html: string
    deviceName?: string
    paperWidthMm?: number
    printableAreaMm?: number
  }): Promise<DesktopPrintResult>
  getPrinters(): Promise<{ name: string; isDefault: boolean }[]>
  /** Minimize the fullscreen/kiosk POS window to the taskbar. */
  minimize(): Promise<void>
  getTenantOrigin(): Promise<string | null>
  reconfigureTenant(): Promise<void>
  /** Set the window/taskbar icon to the tenant's logo (raster URL). No-op on baked builds. */
  setAppIcon(logoUrl: string): Promise<void>

  // ── Update banner (F-FE-N47) ───────────────────────────────────────────────
  // Optional because a packaged shell older than this frontend won't expose
  // them. That can't happen today (the app serves its own bundled frontend from
  // resources/app-dist, so preload and renderer always ship together) — but the
  // cost of guarding is one `?.` and the cost of being wrong is a white screen
  // on a live till.

  /** A staged update waiting to be applied, or null. */
  getUpdateState?(): Promise<{ version: string } | null>
  /** Quit, install the staged update silently, and relaunch. */
  installUpdate?(): Promise<void>
  /** Subscribe to update-state changes; returns an unsubscribe function. */
  onUpdateState?(cb: (s: { version: string } | null) => void): () => void
}

declare global {
  interface Window {
    bhookly?: DesktopBridge
  }
}

/** The Electron bridge when running inside the desktop app, otherwise null. */
export function desktop(): DesktopBridge | null {
  if (typeof window === 'undefined') return null
  const b = window.bhookly
  return b && b.isDesktop ? b : null
}

/**
 * Silently print finished HTML to a named printer when in the desktop app.
 * Returns true if the desktop bridge took the job (the caller must then skip
 * its browser iframe path); false when not running in the desktop app, in
 * which case the caller falls back to window.print().
 *
 * An empty/undefined deviceName prints to the OS default printer — preserving
 * today's single-printer behaviour for tenants that haven't mapped stations to
 * specific devices.
 */
export function desktopPrint(
  html: string,
  opts: { deviceName?: string; paperWidthMm?: number; printableAreaMm?: number } = {},
): boolean {
  const d = desktop()
  if (!d) return false
  d.print({
    html,
    deviceName: opts.deviceName || undefined,
    paperWidthMm: opts.paperWidthMm,
    printableAreaMm: opts.printableAreaMm,
  })
    .then((r) => {
      if (!r.ok) printDebug.error('desktop', 'silent print failed', r.error)
    })
    .catch((e) => printDebug.error('desktop', 'silent print threw', e))
  return true
}
